import { useEffect, useRef } from "react";
import { io } from "socket.io-client";
import { getSocketBaseUrl } from "../api/ProcurementClient";
import { ChatMessage, ChatReactionEvent } from "../models/Chat";

export type ChatSocketEvent =
  | { type: "message"; message: ChatMessage }
  | { type: "message-updated"; message: ChatMessage }
  | { type: "reaction"; reaction: ChatReactionEvent }
  | { type: "conversation-updated"; conversationId: number }
  | { type: "read"; conversationId: number }
  | { type: "connected" };

/**
 * Authenticated socket for staff chat. The server only joins sockets that send
 * a valid staff JWT to the per-user rooms chat events are emitted to.
 */
export function useChatSocket(
  enabled: boolean,
  onEvent: (event: ChatSocketEvent) => void
): void {
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    if (!enabled) {
      return;
    }
    // Default transports (polling, then upgrade): websocket-first never connects
    // through proxies that do not forward upgrades, such as the CRA dev server.
    const socket = io(getSocketBaseUrl(), {
      withCredentials: true,
      reconnectionDelayMax: 10000,
      // Function form so reconnects pick up the current token.
      auth: (cb) => cb({ token: localStorage.getItem("jwtToken") ?? "" }),
    });
    const emit = (event: ChatSocketEvent) => onEventRef.current(event);

    socket.on("connect", () => emit({ type: "connected" }));
    socket.on("chat:message", ({ message }: { message: ChatMessage }) =>
      emit({ type: "message", message })
    );
    socket.on("chat:message-updated", ({ message }: { message: ChatMessage }) =>
      emit({ type: "message-updated", message })
    );
    socket.on("chat:reaction", (reaction: ChatReactionEvent) =>
      emit({ type: "reaction", reaction })
    );
    socket.on("chat:conversation-updated", ({ conversationId }: { conversationId: number }) =>
      emit({ type: "conversation-updated", conversationId })
    );
    socket.on("chat:read", ({ conversationId }: { conversationId: number }) =>
      emit({ type: "read", conversationId })
    );

    return () => {
      socket.disconnect();
    };
  }, [enabled]);
}
